import type {
  PreflightMarket,
  PreflightPlacementCode,
  PreflightPlatformCode,
  ProjectedFeature,
} from "@nexus/contracts";

/** Market shapes and the aggregate read model are owned by the contracts package (KTD13). */
export type {
  AddPreflightMarketInput,
  HeatmapCell,
  HeatmapColumn,
  HeatmapColumnKind,
  HeatmapRow,
  InsightsBandSplit,
  InsightsBlocker,
  InsightsHeatmap,
  InsightsMetrics,
  InsightsPlatformBreakdown,
  InsightsScope,
  InsightsScoreDot,
  InsightsTrend,
  InsightsTrendPoint,
  PreflightInsights,
  PreflightMarket,
  PreflightMarketWithCount,
} from "@nexus/contracts";

/** Report lifecycle states (mirrors `PreflightReportStatus` in the DB schema). */
export type PreflightReportStatus =
  | "QUEUED"
  | "ANALYZING"
  | "COMPLETED"
  | "COMPLETED_WITH_ERRORS"
  | "FAILED";

/** Overall score band (mirrors `PreflightScoreBand`). */
export type PreflightScoreBand = "STRONG" | "NEEDS_REVIEW" | "AT_RISK";

/** Per-asset analysis status (mirrors `PreflightReportAssetStatus`). */
export type PreflightReportAssetStatus = "PENDING" | "SCORED" | "FAILED";

/** Origin of an asset (mirrors `UploadSource`). */
export type PreflightAssetSource = "DIRECT" | "URL";

/** Report-status values that stop polling — no further transitions are expected. */
export const TERMINAL_REPORT_STATUSES = [
  "COMPLETED",
  "COMPLETED_WITH_ERRORS",
  "FAILED",
] as const satisfies readonly PreflightReportStatus[];

export function isTerminalReportStatus(status: PreflightReportStatus): boolean {
  return (TERMINAL_REPORT_STATUSES as readonly string[]).includes(status);
}

export interface PreflightReportOwner {
  displayName: string;
  photoUrl: string | null;
}

/** List-row + detail base shape returned by the BFF (`ReportSummaryDto`). */
export interface PreflightReportSummary {
  id: string;
  name: string;
  brandName: string;
  platform: PreflightPlatformCode;
  placement: PreflightPlacementCode;
  status: PreflightReportStatus;
  overallScore: number | null;
  overallBand: PreflightScoreBand | null;
  analyzerVersion: string | null;
  assetCount: number;
  scoredCount: number;
  failedCount: number;
  criteriaPassedCount: number;
  criteriaFailedCount: number;
  createdBy: PreflightReportOwner | null;
  /** Null only for reports that predate the market backfill (expand/contract, KTD6). */
  market: PreflightMarket | null;
  /** Starred by the signed-in user — favorites are per user, not per tenant. */
  isFavorite: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Per-asset row on the report detail (`ReportAssetDto`); `features` are projected server-side (KTD8). */
export interface PreflightReportAsset {
  id: string;
  uploadId: string;
  status: PreflightReportAssetStatus;
  score: number | null;
  band: PreflightScoreBand | null;
  hasUnanalyzed: boolean;
  error: string | null;
  source: PreflightAssetSource | null;
  originalName: string | null;
  contentType: string | null;
  sourceUrl: string | null;
  /** Short-TTL signed read URL for the original asset; may be null/expired. */
  assetUrl: string | null;
  /** Short-TTL signed read URL; may be null/expired — fall back to a placeholder. */
  thumbnailUrl: string | null;
  features: ProjectedFeature[];
}

/** Full report detail returned by `GET /preflight/reports/:id`. */
export interface PreflightReportDetail extends PreflightReportSummary {
  assets: PreflightReportAsset[];
}

/** Lightweight status payload from `GET /preflight/reports/:id/status` (read-through cache). */
export interface PreflightReportStatusResult {
  id: string;
  status: PreflightReportStatus;
  overallScore: number | null;
  overallBand: PreflightScoreBand | null;
  assetCount: number;
  scoredCount: number;
  failedCount: number;
}

/** One page of the cursor-paginated reports list. */
export interface PreflightReportListPage {
  items: PreflightReportSummary[];
  nextCursor: string | null;
  hasMore: boolean;
}

/** Payload for `POST /preflight/reports` (draft-wiring R3). */
export interface CreateReportInput {
  uploadIds: string[];
  urls: string[];
  platform: PreflightPlatformCode;
  placement: PreflightPlacementCode;
  brandName: string;
  /**
   * The API rejects a create without a market (R1). Optional here only until the
   * create modal owns market selection and blocks submission without one (R12).
   */
  marketId?: string;
  name?: string;
}

/** `POST /preflight/reports` returns only the new report id (status defaults to QUEUED). */
export interface CreateReportResult {
  reportId: string;
}

/** `POST /preflight/reports/:id/retry` response. */
export interface RetryReportResult {
  reportId: string;
  status: PreflightReportStatus;
}
